import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ListComponentProps } from '../../Routers/CrudRouter';
import { useTableColumnsMemo } from './tableColumns';
import { tryCatchJsonByAction } from '../../../utils/fetchUtils';
import { TableButtons } from '../../../components/Table/TableButtons';
import { Table } from '../../../components/Table/Table';
import { PPSCardMembershipRequestResponse, PutujmoSite } from '../../../swagger/putujmoAdminService';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { MultiSelect } from '../../../components/MultiSelect/MultiSelect';
import { siteOptions } from '../../../utils/languageUtils';
import { Button } from '../../../components/Button/Button';
import { SimpleDialog } from '../../../components/Dialog/SimpleDialog/SimpleDialog';
import {
	deletePPSCardMembershipRequestAction,
	getAllPPSCardMembershipRequestsAction,
	markPPSCardMembershipRequestPaidAction,
} from '../action';

export const PPSCardMembershipRequestsTable = ({ dataChangedTopic, publishDataChanged }: ListComponentProps) => {
	const [putujmoSite, setPutujmoSite] = useState(PutujmoSite.Serbia);
	const [selectedId, setSelectedId] = useState<string | number | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<PPSCardMembershipRequestResponse[]>([]);
	const [isMarkPaidDialogOpen, setIsMarkPaidDialogOpen] = useState(false);

	const columns = useTableColumnsMemo();

	const fetchDataCallback = useCallback(async () => {
		setLoading(true);
		const bindedAction = getAllPPSCardMembershipRequestsAction.bind(null, putujmoSite);
		const getResponse = await tryCatchJsonByAction(bindedAction);
		if (getResponse.success) {
			setData(getResponse.response || []);
		}
		setLoading(false);
	}, [putujmoSite]);

	useEffect(() => {
		fetchDataCallback();
	}, [fetchDataCallback]);

	useEffect(() => {
		const subscription = dataChangedTopic?.subscribe(fetchDataCallback);
		return () => {
			subscription?.unsubscribe();
		};
	}, [fetchDataCallback, dataChangedTopic]);

	const selectedRowMemo = useMemo(
		() => data.find((x) => x.id === selectedId),
		[data, selectedId]
	);

	const canMarkPaidMemo = !!selectedRowMemo && !selectedRowMemo.isPaid;

	const deleteCallback = useCallback(
		async (id: number) => {
			const bindedAction = deletePPSCardMembershipRequestAction.bind(null, id);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				toast.success('Element je uspešno obrisan');
				setSelectedId(undefined);
				publishDataChanged();
			}
		},
		[publishDataChanged]
	);

	const openMarkPaidDialog = useCallback(() => {
		if (!selectedId || !canMarkPaidMemo) return;
		setIsMarkPaidDialogOpen(true);
	}, [selectedId, canMarkPaidMemo]);

	const closeMarkPaidDialog = useCallback(() => {
		setIsMarkPaidDialogOpen(false);
	}, []);

	const confirmMarkPaidCallback = useCallback(async () => {
		setIsMarkPaidDialogOpen(false);
		if (!selectedId) return;
		const bindedAction = markPPSCardMembershipRequestPaidAction.bind(null, selectedId as number);
		const response = await tryCatchJsonByAction(bindedAction);
		if (response.success) {
			toast.success('Prijava je označena kao plaćena');
			publishDataChanged();
		}
	}, [selectedId, publishDataChanged]);

	return (
		<div>
			<div className='flex-start-container'>
				<div className='m5'>
					<MultiSelect
						value={[putujmoSite]}
						items={siteOptions}
						onChange={(value) => {
							setPutujmoSite(value.length > 0 ? value[0] : PutujmoSite.Serbia);
						}}
						getItemId={(item: any) => item.id}
						getItemText={(item: any) => item.text}
					/>
				</div>
			</div>
			<TableButtons
				selectedId={selectedId}
				onDelete={deleteCallback}
				isViewable={false}
				isChangeable={false}
				isCreateable={false}
				AdditionalButton={
					<Button onClick={openMarkPaidDialog} value='Označi kao plaćeno' disabled={!canMarkPaidMemo} />
				}
			/>
			<Table columns={columns} data={data} onSelectedChange={setSelectedId} />
			{loading && <LoadingSpinner />}
			<SimpleDialog
				open={isMarkPaidDialogOpen}
				title='Označavanje kao plaćeno'
				message='Da li ste sigurni da želite da označite prijavu kao plaćenu?'
				onConfirm={confirmMarkPaidCallback}
				onCancel={closeMarkPaidDialog}
			/>
		</div>
	);
};
